import React, { useState } from 'react';
import { useGlobalInvitations, useCreateGlobalInvitation } from './hooks/useGlobalInvitations';
import { RoleType, GlobalInvitation, MANAGEMENT_ROLES, formatRoleLabel, APIError, ErrorCode, type ErrorResponse } from '@promo/shared';
import { FormField } from '@/components/FormField';
import { EditableInput } from '@/components/ui/editable-input';
import { EditableSelect, type SelectOption } from '@/components/ui/editable-select';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableRow, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { parseNameFromEmail } from '@promo/shared';
import { useQueries } from '@tanstack/react-query';
import { get } from '@/lib/api';
import type { User } from '@promo/shared';

export function GlobalInvitationsPage() {
  const [emailInput, setEmailInput] = useState('');
  const [formError, setFormError] = useState('');
  const [role, setRole] = useState<RoleType>(RoleType.MANAGER);
  const roleOptions: SelectOption[] = MANAGEMENT_ROLES.map(r => ({
    value: r,
    label: formatRoleLabel(r),
  }));
  const { data: invitations = [], isLoading, error } = useGlobalInvitations();
  const peerQueries = useQueries({
    queries: invitations.map(inv => ({
      queryKey: ['user', inv.peerId],
      queryFn: () => get<User>(`/users/${inv.peerId}`),
      enabled: Boolean(inv.peerId),
    })),
  });
  const createInv = useCreateGlobalInvitation();

  const formatDate = (date: string | Date) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  };

  function StatusBadge({ status }: { status: string }) {
    let variant: 'default' | 'secondary' | 'outline' | 'destructive' = 'default';
    switch (status) {
      case 'PENDING':
        variant = 'secondary'; break;
      case 'ACCEPTED':
        variant = 'default'; break;
      case 'DECLINED':
        variant = 'destructive'; break;
      case 'EXPIRED':
        variant = 'outline'; break;
    }
    return <Badge variant={variant}>{status.toLowerCase()}</Badge>;
  }

  function DecisionBadge({ decision }: { decision?: string }) {
    let variant: 'default' | 'secondary' | 'destructive' | 'outline' = 'default';
    const text = (decision ?? 'pending').toLowerCase();
    switch (decision) {
      case 'ATTESTED':
        variant = 'default';
        break;
      case 'DENIED':
        variant = 'destructive';
        break;
      default:
        variant = 'secondary';
        break;
    }
    return <Badge variant={variant}>{text}</Badge>;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!emailInput.toLowerCase().endsWith('@tebra.com')) {
      setFormError('Email must end with @tebra.com');
      return;
    }
    if (!emailInput) return;
    createInv.mutate(
      { inviteeEmail: emailInput, roleRequested: role },
      {
        onSuccess: () => {
          toast({ title: 'Invitation sent', description: `Invitation sent to ${emailInput}.` });
          setEmailInput('');
          setFormError('');
        },
        onError: (err) => {
          if (err instanceof APIError) {
            const data = err.details as ErrorResponse | undefined;
            if (data?.error?.code === ErrorCode.VALIDATION_ERROR) {
              setFormError(data.error.message);
              return;
            }
          }
          const msg = err instanceof Error ? err.message : 'Failed to send invitation';
          toast({ title: 'Error', description: msg, variant: 'destructive' });
        }
      }
    );
  };

  // Local component to display a user's name, role, and status badge
  function UserStatus({
    label,
    name,
    role,
    badge,
  }: {
    label: string;
    name: string;
    role: string;
    badge: React.ReactNode;
  }) {
    return (
      <div className="flex flex-col">
        <span className="text-xs uppercase text-muted-foreground">{label}</span>
        <span className="font-semibold">{name}</span>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">{role}</span>
          {badge}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-8">
      <h1 className="text-2xl font-semibold mb-4">Management</h1>
      <p className="mb-4">
        Management roles which have access to all packets are granted by <strong>random peer attestation</strong>.
        A random peer with the same role or higher will be selected to attest the desired role of the invitee.
        If the peer attests, the invitee will be granted the role and invited to the app.
      </p>
      <p className="mb-8">
        Enter an email below and select a role to invite the user to the app.
      </p>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-6">
        <FormField htmlFor="email" label="Email">
          <EditableInput
            id="email"
            type="email"
            required
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="user@tebra.com"
          />
          {formError && (
            <p className="text-red-500 text-sm mt-1">{formError}</p>
          )}
        </FormField>
        <FormField htmlFor="role" label="Role">
          <EditableSelect
            id="role"
            options={roleOptions}
            value={role}
            onValueChange={(val) => setRole(val as RoleType)}
            placeholder="Select role"
          />
        </FormField>
        <div className="col-span-1 md:col-span-2 flex justify-end">
          <Button type="submit" disabled={createInv.isPending}>
            {createInv.isPending ? 'Inviting...' : 'Invite'}
          </Button>
        </div>
      </form>

      {isLoading ? (
        <div>Loading invitations...</div>
      ) : error ? (
        <div className="text-destructive">Failed to load invitations</div>
      ) : (
        <Table>
          <TableBody>
            {invitations.map((inv: GlobalInvitation, i) => {
              const parsed = parseNameFromEmail(inv.inviteeEmail);
              const inviteeName = parsed ?? inv.inviteeEmail;

              // Determine peer's name via the peerQueries array
              const peerQuery = peerQueries[i];
              let peerName: string;
              if (!inv.peerId) {
                peerName = 'N/A';
              } else if (peerQuery.isLoading) {
                peerName = 'Loading…';
              } else {
                peerName = peerQuery.data?.name ?? inv.peerId;
              }

              return (
                <TableRow key={inv.id} className="block md:table-row">
                  {/* Invitee column */}
                  <TableCell className="block md:table-cell md:w-1/2">
                    <UserStatus
                      label="Invitee"
                      name={inviteeName}
                      role={formatRoleLabel(inv.roleRequested)}
                      badge={<StatusBadge status={inv.status} />}
                    />
                  </TableCell>

                  {/* Peer column */}
                  <TableCell className="block md:table-cell md:w-1/2">
                    <UserStatus
                      label="Peer"
                      name={peerName}
                      role={formatRoleLabel(inv.roleRequested)}
                      badge={<DecisionBadge decision={inv.peerDecision} />}
                    />
                  </TableCell>

                  {/* Expires column */}
                  <TableCell className="block md:table-cell md:w-auto whitespace-nowrap align-top">
                    <div className="flex flex-col">
                      <span className="text-xs uppercase text-muted-foreground">
                        expires
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(inv.expiresAt)}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
} 